import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useSlate} from "slate-react";

import {addStyle, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {border, color, spacing} from "@khanacademy/wonder-blocks-tokens";

import {getBlockType, isBlockActive} from "./slate/helpers/block-helpers.ts";
import {isMarkActive} from "./slate/helpers/mark-helpers.ts";
import ToolButton from "./tool-button.tsx";
import ToolbarBlockSelect from "./toolbar-block-select.tsx";
import {setBlockType, toggleMark} from "./utils.ts";

import redoIcon from "@phosphor-icons/core/regular/arrow-clockwise.svg";
import undoIcon from "@phosphor-icons/core/regular/arrow-counter-clockwise.svg";
import bulletedListIcon from "@phosphor-icons/core/regular/list-bullets.svg";
import numberedListIcon from "@phosphor-icons/core/regular/list-numbers.svg";
import boldIcon from "@phosphor-icons/core/regular/text-b.svg";
import italicIcon from "@phosphor-icons/core/regular/text-italic.svg";

interface Props {
    disabled?: boolean;
    disableStickyToolbar?: boolean;
}

export function Toolbar(props: Props): React.ReactElement {
    return (
        <View
            role="toolbar"
            style={[
                styles.toolbar,
                !props.disableStickyToolbar && styles.stickyToolbar,
            ]}
        >
            <ToolButtons disabled={props.disabled} />
        </View>
    );
}

interface ToolButtonsProps {
    disabled?: boolean;
}

function ToolButtons({disabled}: ToolButtonsProps): React.ReactElement {
    const editor = useSlate();

    const textFormattingButtons = [
        {
            icon: boldIcon,
            tooltip: t`Bold`,
            active: isMarkActive(editor, "bold"),
            onClick: () => toggleMark(editor, "bold"),
        },
        {
            icon: italicIcon,
            tooltip: t`Italic`,
            active: isMarkActive(editor, "italic"),
            onClick: () => toggleMark(editor, "italic"),
        },
    ];

    const listFormattingButtons = [
        {
            icon: bulletedListIcon,
            tooltip: t`Bulleted list`,
            active: isBlockActive(editor, "bulleted-list"),
            onClick: () => setBlockType(editor, "bulleted-list"),
        },
        {
            icon: numberedListIcon,
            tooltip: t`Numbered list`,
            active: isBlockActive(editor, "numbered-list"),
            onClick: () => setBlockType(editor, "numbered-list"),
        },
    ];

    const historyCommandButtons = [
        {
            icon: undoIcon,
            tooltip: t`Undo`,
            onClick: () => editor.undo(),
        },
        {
            icon: redoIcon,
            tooltip: t`Redo`,
            onClick: () => editor.redo(),
        },
    ];

    return (
        <>
            {historyCommandButtons.map((button, i) => (
                <ToolButton key={i} {...button} disabled={disabled} />
            ))}

            <Separator />

            <SingleSelect
                aria-label={t`Text style`}
                disabled={disabled}
                onChange={(newValue) => setBlockType(editor, newValue as any)}
                opener={ToolbarBlockSelect}
                placeholder={t`Text style`}
                selectedValue={getBlockType(editor) || "paragraph"}
                style={{flexShrink: 0}}
            >
                {[
                    {label: t`Paragraph`, value: "paragraph"},
                    {label: t`Title`, value: "heading-one"},
                    {label: t`Heading`, value: "heading-two"},
                    {
                        label: t`Subheading`,
                        value: "heading-three",
                    },
                    {
                        label: t`Block quote`,
                        value: "block-quote",
                    },
                    {
                        label: t`Bulleted list`,
                        value: "bulleted-list",
                    },
                    {
                        label: t`Numbered list`,
                        value: "numbered-list",
                    },
                ].map((item, i) => (
                    <OptionItem key={i} {...item} />
                ))}
            </SingleSelect>

            <View style={styles.toolbarButtonGroup}>
                <Separator />

                {textFormattingButtons.map((toolButton, i) => (
                    <ToolButton key={i} {...toolButton} disabled={disabled} />
                ))}
            </View>

            <View style={styles.toolbarButtonGroup}>
                <Separator />

                {listFormattingButtons.map((toolButton, i) => (
                    <ToolButton key={i} {...toolButton} disabled={disabled} />
                ))}
            </View>
        </>
    );
}

const StyledDiv = addStyle("div");

const Separator = () => (
    <StyledDiv role="separator" style={styles.toolbarSeparator} />
);

const styles = StyleSheet.create({
    stickyToolbar: {
        position: "sticky",
        top: 0,
        zIndex: 10,
    },
    toolbar: {
        flexDirection: "row",
        alignItems: "center",
        padding: spacing.xxxSmall_4,
        flexGrow: 0,
        justifyContent: "start",
        border: `${border.width.thin} solid ${color.offBlack50}`,
        background: color.white,
        borderStartStartRadius: 4,
        borderStartEndRadius: 4,
        flexWrap: "wrap",
    },
    toolbarSeparator: {
        borderInlineEnd: `1px solid ${color.offBlack16}`,
        margin: spacing.xxxSmall_4,
        height: spacing.xLarge_32,
    },
    toolbarButtonGroup: {
        alignItems: "center",
        flexDirection: "row",
    },
});
